import { Page } from "@cntrl/ui";
import { YourCrewCard } from "./YourCrewCard.js";
import { TeamsCard } from "./TeamsCard.js";
import { Top8Card } from "./Top8Card.js";
import { FriendsCard } from "./FriendsCard.js";
import { PartnerBox } from "./PartnerBox.js";
import styles from "./ContactsScreen.module.scss";

/**
 * The `/contacts` screen. The spec's layout is fixed and this component exists to hold it: **two columns,
 * four cards** — Your Crew and Your Teams left, Top 8 and Friends right — collapsing to one column under
 * 1180px.
 *
 * Follow the layout the design spec designates. An extra card here is a migration artifact, not a feature:
 * the contacts rolodex used to be a fifth surface and now folds into Your Crew, which is the one list of
 * people on this band.
 *
 * Each card gates independently and renders null when its seat can't see it, so the grid degrades cleanly
 * for read-only seats rather than leaving empty scaffolding.
 *
 * One addition beyond the spec's four, by ruling (ART-239, product owner 2026-10-08): the PARTNER card, under
 * Friends in the right column — mobile already had the box while desktop had no way to invite a partner at all.
 */
export function ContactsScreen({
  onOpenBand,
  onOpenPerson,
}: {
  onOpenBand?: (bandId: string) => void;
  onOpenPerson?: (personId: string) => void;
}) {
  return (
    // `<Page>` owns the vertical rhythm + the mobile-gated side gutter (the shell supplies none), so this
    // screen renders correctly at phone width without hand-rolling the rhythm (ART-153 pass two — the
    // pageShell guard forbids a screen re-declaring it). No visible title: the spec's grid has none.
    <Page>
      {/* The route's top-level heading. The spec's grid has no page title (each card owns its header), so
          this is visually hidden — it restores the document outline root + the app-wide h1-per-screen
          convention with zero pixel impact (the visible screen identity is the shell nav's active tab). */}
      <h1 className={styles.srOnly}>Contacts</h1>

      <div className={styles.grid}>
        <div className={styles.col}>
          <YourCrewCard onOpenPerson={onOpenPerson} />
          <TeamsCard />
        </div>
        <div className={styles.col}>
          <Top8Card onOpenBand={onOpenBand} />
          <FriendsCard onOpenBand={onOpenBand} />
          <PartnerBox variant="card" />
        </div>
      </div>
    </Page>
  );
}
